/**
 * Right-panel для /messenger — только Настройки (Медиа удалили).
 * Тогглы (Уведомления / Звук / Статус онлайн) — только для админов.
 * Уведомления — реальный mute/unmute push API.
 */
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import {
  fetchPushStatus, muteNotifications, unmuteNotifications,
} from "@/api/dashboard";
import { useAuth } from "@/auth/AuthContext";
import { Icon } from "@/components/prototype/Icon";

function useLocalBool(key: string, def: boolean): [boolean, (v: boolean) => void] {
  const [v, setV] = useState<boolean>(() => {
    try { const x = localStorage.getItem(key); return x === null ? def : x === "1"; } catch { return def; }
  });
  useEffect(() => { try { localStorage.setItem(key, v ? "1" : "0"); } catch { /* ignore */ } }, [key, v]);
  return [v, setV];
}

export function MessengerRightPanel({
  topicId, isOpen, onClose,
  accent, onAccentChange,
  bg, onBgChange,
}: {
  topicId: number | null;
  isOpen: boolean;
  onClose: () => void;
  accent: string;
  onAccentChange: (a: string) => void;
  bg: string;
  onBgChange: (b: string) => void;
}) {
  void topicId;
  const { user } = useAuth();
  const isAdmin = !!user?.is_superuser || user?.role === "admin" || user?.role === "owner";
  const qc = useQueryClient();

  const [sound, setSound] = useLocalBool("mng-toggle-sound", true);
  const [online, setOnline] = useLocalBool("mng-toggle-online", true);

  // Уведомления — состояние из backend (push status), переключение через mute/unmute
  const pushStatusQ = useQuery({
    queryKey: ["push-status"],
    queryFn: fetchPushStatus,
    enabled: isOpen,
    staleTime: 30_000,
  });
  const notifEnabled = pushStatusQ.data ? !pushStatusQ.data.muted : true;

  const muteMut = useMutation({
    mutationFn: muteNotifications,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["push-status"] }),
    onError: (e: Error) => alert(`Не удалось отключить уведомления: ${e.message}`),
  });
  const unmuteMut = useMutation({
    mutationFn: unmuteNotifications,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["push-status"] }),
    onError: (e: Error) => alert(`Не удалось включить уведомления: ${e.message}`),
  });

  // Push subscription управляется реальным pushManager.subscribe/unsubscribe
  const push = usePushNotifications();
  const isPushSubscribed = push.state.status === "subscribed";
  const isPushSwitchOn = isPushSubscribed && notifEnabled;

  async function toggleNotif() {
    if (isPushSwitchOn) {
      // Включено → выключить: muteNotifications + unsubscribe device
      try { muteMut.mutate(); } catch { /* ignore */ }
      try { await push.unsubscribe(); } catch (e) { console.warn("unsubscribe failed", e); }
    } else {
      // Выключено → включить:
      // 1) Если подписки ещё нет — subscribe() (попросит permission, создаст subscription, отправит на backend)
      // 2) Unmute на бэке
      if (!isPushSubscribed) {
        const ok = await push.subscribe();
        if (!ok) return;  // user denied permission etc.
      }
      try { unmuteMut.mutate(); } catch { /* ignore */ }
    }
  }

  if (!isOpen) return null;

  return (
    <aside className="mng-right-panel">
      <header className="mng-rp-header">
        <div className="mng-rp-tabs">
          <button className="mng-rp-tab active">Настройки</button>
        </div>
        <button className="mng-rp-close" onClick={onClose} title="Закрыть">
          <Icon name="x" size={16}/>
        </button>
      </header>

      <div className="mng-rp-content">
        <div className="mng-rp-group">
          <div className="mng-rp-label">Цвет акцента</div>
          <div className="mng-rp-colors">
            {(["green", "blue", "purple", "teal", "orange", "pink", "red", "indigo", "graphite"] as const).map(c => (
              <button
                key={c}
                className={`mng-rp-color is-${c}${accent === c ? " active" : ""}`}
                onClick={() => onAccentChange(c)}
                title={c}
              />
            ))}
          </div>
        </div>
        <div className="mng-rp-group">
          <div className="mng-rp-label">Фон чата</div>
          <div className="mng-rp-bgs">
            {(["green", "blue", "purple", "beige", "gray", "mint", "peach", "lavender", "sky", "white"] as const).map(b => (
              <button
                key={b}
                className={`mng-rp-bg is-${b}${bg === b ? " active" : ""}`}
                onClick={() => onBgChange(b)}
                title={b}
              />
            ))}
          </div>
        </div>
        <div className="mng-rp-group">
          <div className="mng-rp-label">Настройки</div>
          <ToggleRow
            label="Уведомления"
            value={notifEnabled}
            disabled={muteMut.isPending || unmuteMut.isPending || pushStatusQ.isLoading}
            onChange={toggleNotif}
          />
          {isAdmin && (
            <>
              <ToggleRow label="Звук сообщений" value={sound} onChange={setSound}/>
              <ToggleRow label="Статус онлайн" value={online} onChange={setOnline}/>
            </>
          )}
        </div>
      </div>
    </aside>
  );
}

function ToggleRow({ label, value, onChange, disabled }: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="mng-rp-toggle-row">
      <span className="mng-rp-toggle-label">{label}</span>
      <button
        type="button"
        className={`mng-rp-toggle${value ? " active" : ""}`}
        onClick={() => { if (!disabled) onChange(!value); }}
        disabled={disabled}
        aria-pressed={value}
        aria-label={label}
      />
    </div>
  );
}
